import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ReportId } from '@core/models';
import { environment } from '@environments/environment';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export interface TenantConfiguration {
  nome: string;
  nif: string;
  logoDataUrl: string | null;
  reportConfigurations: TenantReportConfiguration[];
  enabledReportIds: ReportId[];
}

export interface TenantReportConfiguration {
  id: ReportId;
  code: number;
  name: string;
  enabled: boolean;
}

export interface SaveTenantConfiguration {
  configuration: TenantConfiguration;
  logoFile: File | null;
  logoChanged: boolean;
}

enum ConfiguracaoCodigo {
  NomeOrganizacao = 1,
  Nif = 2,
  Logotipo = 3,
  Efetivos = 100,
  Faltas = 101,
  Escalas = 102,
  Ocorrencias = 103,
  Rondas = 104,
  Financeiro = 105,
  Postos = 106,
  Equipamentos = 107,
}

enum ConfiguracaoTipoDado {
  String = 3,
  Boolean = 5,
  Ficheiro = 6,
}

export enum ConfiguracaoCategoria {
  Instituicao = 1,
  Relatorio = 2,
}

interface ConfiguracaoRecord {
  configuracaoCodigoEnum: number;
  configuracaoTipoDadoEnum: number;
  configuracaoCategoriaEnum: number;
  configuracaoCategoria?: string | null;
  configuracaoCodigo?: string | null;
  configuracaoTipoDado?: string | null;
  valor: string | number | boolean | null;
}

interface ConfiguracaoPageResponse {
  data?: ConfiguracaoRecord[] | null;
}

interface ConfiguracaoRequest {
  configuracaoCodigoEnum: ConfiguracaoCodigo;
  configuracaoTipoDadoEnum: ConfiguracaoTipoDado;
  configuracaoCategoriaEnum: ConfiguracaoCategoria;
  valor?: string;
  ficheiro?: File;
}

const REPORT_CODES: Readonly<Record<ReportId, ConfiguracaoCodigo>> = {
  efetivos: ConfiguracaoCodigo.Efetivos,
  faltas: ConfiguracaoCodigo.Faltas,
  escalas: ConfiguracaoCodigo.Escalas,
  ocorrencias: ConfiguracaoCodigo.Ocorrencias,
  rondas: ConfiguracaoCodigo.Rondas,
  financeiro: ConfiguracaoCodigo.Financeiro,
  postos: ConfiguracaoCodigo.Postos,
  equipamentos: ConfiguracaoCodigo.Equipamentos,
};

const REPORT_IDS = Object.keys(REPORT_CODES) as ReportId[];
const REPORT_IDS_BY_CODE = new Map<number, ReportId>(
  REPORT_IDS.map((id) => [REPORT_CODES[id], id]),
);

const DEFAULT_CONFIGURATION: TenantConfiguration = {
  nome: '',
  nif: '',
  logoDataUrl: null,
  reportConfigurations: [],
  enabledReportIds: [],
};

@Injectable({ providedIn: 'root' })
export class TenantConfigurationService {
  private readonly http = inject(HttpClient);
  private readonly endpoint = `${environment.apiurl}/api/Configuracao`;
  private readonly storageKey = 'securpro_tenant_configuration';

  readonly configuration = signal<TenantConfiguration>(this.readCache());

  load(): Observable<TenantConfiguration> {
    return forkJoin([
      this.loadByCategory(ConfiguracaoCategoria.Instituicao),
      this.loadByCategory(ConfiguracaoCategoria.Relatorio),
    ]).pipe(
      map(([institution, reports]) =>
        this.mapResponse([institution, reports]),
      ),
      tap((configuration) => this.updateConfiguration(configuration)),
    );
  }

  private loadByCategory(
    category: ConfiguracaoCategoria,
  ): Observable<ConfiguracaoPageResponse> {
    const params = new HttpParams()
      .set('ConfiguracaoCategoriaEnum', category)
      .set('pageNumber', 1)
      .set('pageSize', 100);

    return this.http.get<ConfiguracaoPageResponse>(this.endpoint, { params });
  }

  save(input: SaveTenantConfiguration): Observable<TenantConfiguration> {
    const configuration = this.normalize(input.configuration);
    const requests = this.buildRequests(configuration, input);
    const formData = this.toFormData(requests);

    return this.http.post<unknown>(this.endpoint, formData).pipe(
      switchMap(() => this.load()),
    );
  }

  private buildRequests(
    configuration: TenantConfiguration,
    input: SaveTenantConfiguration,
  ): ConfiguracaoRequest[] {
    const requests: ConfiguracaoRequest[] = [
      {
        configuracaoCodigoEnum: ConfiguracaoCodigo.NomeOrganizacao,
        configuracaoTipoDadoEnum: ConfiguracaoTipoDado.String,
        configuracaoCategoriaEnum: ConfiguracaoCategoria.Instituicao,
        valor: configuration.nome,
      },
      {
        configuracaoCodigoEnum: ConfiguracaoCodigo.Nif,
        configuracaoTipoDadoEnum: ConfiguracaoTipoDado.String,
        configuracaoCategoriaEnum: ConfiguracaoCategoria.Instituicao,
        valor: configuration.nif,
      },
      ...configuration.reportConfigurations.map(
        (report): ConfiguracaoRequest => ({
          configuracaoCodigoEnum: REPORT_CODES[report.id],
          configuracaoTipoDadoEnum: ConfiguracaoTipoDado.Boolean,
          configuracaoCategoriaEnum: ConfiguracaoCategoria.Relatorio,
          valor: String(configuration.enabledReportIds.includes(report.id)),
        }),
      ),
    ];

    if (input.logoChanged) {
      requests.splice(2, 0, {
        configuracaoCodigoEnum: ConfiguracaoCodigo.Logotipo,
        configuracaoTipoDadoEnum: ConfiguracaoTipoDado.Ficheiro,
        configuracaoCategoriaEnum: ConfiguracaoCategoria.Instituicao,
        ...(input.logoFile
          ? { ficheiro: input.logoFile }
          : { valor: '' }),
      });
    }

    return requests;
  }

  /**
   * ASP.NET faz o binding de listas multipart através de nomes indexados.
   * Um único campo requests com JSON faz a lista chegar vazia ao controller.
   */
  private toFormData(requests: ConfiguracaoRequest[]): FormData {
    const formData = new FormData();

    requests.forEach((request, index) => {
      const prefix = `requests[${index}]`;
      formData.append(
        `${prefix}.configuracaoCodigoEnum`,
        String(request.configuracaoCodigoEnum),
      );
      formData.append(
        `${prefix}.configuracaoTipoDadoEnum`,
        String(request.configuracaoTipoDadoEnum),
      );
      formData.append(
        `${prefix}.configuracaoCategoriaEnum`,
        String(request.configuracaoCategoriaEnum),
      );
      if (request.valor !== undefined) {
        formData.append(`${prefix}.valor`, request.valor);
      }

      if (request.ficheiro) {
        formData.append(
          `${prefix}.ficheiro`,
          request.ficheiro,
          request.ficheiro.name,
        );
      }
    });

    return formData;
  }

  private mapResponse(responses: ConfiguracaoPageResponse[]): TenantConfiguration {
    const institutionRecords = this.readRecords(responses[0]);
    const reportRecords = this.readRecords(responses[1]);
    const records = [...institutionRecords, ...reportRecords];
    const byCode = new Map(
      records.map((record) => [Number(record.configuracaoCodigoEnum), record]),
    );
    const reportConfigurations = reportRecords
      .map((record) => this.mapReportConfiguration(record))
      .filter((report): report is TenantReportConfiguration => report !== null);

    return this.normalize({
      nome: this.readString(byCode.get(ConfiguracaoCodigo.NomeOrganizacao)),
      nif: this.readString(byCode.get(ConfiguracaoCodigo.Nif)),
      logoDataUrl: this.readString(byCode.get(ConfiguracaoCodigo.Logotipo)) || null,
      reportConfigurations,
      enabledReportIds: reportConfigurations
        .filter((report) => report.enabled)
        .map((report) => report.id),
    });
  }

  private readRecords(
    response: ConfiguracaoPageResponse | undefined,
  ): ConfiguracaoRecord[] {
    return Array.isArray(response?.data) ? response.data : [];
  }

  private mapReportConfiguration(
    record: ConfiguracaoRecord,
  ): TenantReportConfiguration | null {
    const code = Number(record.configuracaoCodigoEnum);
    const id = REPORT_IDS_BY_CODE.get(code);
    if (!id) return null;

    return {
      id,
      code,
      name: String(record.configuracaoCodigo ?? '').trim() || id,
      enabled: this.readBoolean(record),
    };
  }

  private readString(record: ConfiguracaoRecord | undefined): string {
    const value = record?.valor;
    return value === null || value === undefined ? '' : String(value).trim();
  }

  private readBoolean(record: ConfiguracaoRecord | undefined): boolean {
    const value = record?.valor;
    return value === true || value === 1 || String(value).toLowerCase() === 'true';
  }

  private normalize(configuration: TenantConfiguration): TenantConfiguration {
    return {
      nome: configuration.nome.trim(),
      nif: configuration.nif.trim().toUpperCase(),
      logoDataUrl: configuration.logoDataUrl?.trim() || null,
      reportConfigurations: configuration.reportConfigurations
        .filter((report) => REPORT_IDS.includes(report.id))
        .map((report) => ({
          ...report,
          name: report.name.trim(),
          enabled: configuration.enabledReportIds.includes(report.id),
        })),
      enabledReportIds: [
        ...new Set(
          configuration.enabledReportIds.filter((id) => REPORT_IDS.includes(id)),
        ),
      ],
    };
  }

  private updateConfiguration(configuration: TenantConfiguration): void {
    this.configuration.set(configuration);

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(configuration));
    } catch {
      // A cache é apenas uma optimização; a API continua a ser a fonte de verdade.
    }
  }

  private readCache(): TenantConfiguration {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return DEFAULT_CONFIGURATION;

      const parsed = JSON.parse(stored) as Partial<TenantConfiguration>;
      return this.normalize({
        nome: parsed.nome ?? '',
        nif: parsed.nif ?? '',
        logoDataUrl: parsed.logoDataUrl ?? null,
        reportConfigurations: [],
        enabledReportIds: [],
      });
    } catch {
      return DEFAULT_CONFIGURATION;
    }
  }
}
